import { useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useSession } from '@/hooks/useSession';
import { createPartnerLink } from '@/services/partners';

// Público e igual em todos os ambientes; o client_secret fica no servidor do jogo.
const CLIENT_ID = 'herois-do-enem';

// Códigos de erro do SSO com mensagem própria; os demais caem na genérica.
const ERROR_MESSAGES = {
  forbidden_profile: 'heroisEnem.forbiddenProfile',
  invalid_origin: 'heroisEnem.invalidOrigin'
};

// Lógica de acesso ao Heróis do ENEM, independente do elemento visual (botão,
// banner, item de menu). canAccess indica se o acesso integrado está disponível;
// quem consome chama open() no clique quando essa condição for atendida.
//
// - state: etiqueta enviada ao jogo dizendo de onde veio o clique (só métricas).
// - newTab: abre o jogo em nova aba (padrão) ou na aba atual.
export default function useHeroisEnemLink({ state = 'em3-banner', newTab = true } = {}) {
  const { session, ssoToken, dbNumber, signOut } = useSession();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const profiles = session?.user?.profileNames || session?.user?.['profile-names'] || [];

  // Mesmas regras do cadastro no SSO: só alunos da EM3 (db_number 1) do Canal
  // Educação que entraram pelo SSO. A autorização também é validada pelo SSO.
  const canAccess = process.env.REACT_APP_NAME === 'canaleducacao' && String(dbNumber) === '1' && profiles.includes('Aluno') && Boolean(ssoToken);

  const open = async () => {
    if (!canAccess || loading) return;

    setLoading(true);

    // A aba precisa ser aberta no clique, antes do await, ou o navegador a
    // bloqueia como pop-up. Se mesmo assim for bloqueada (window.open devolve
    // null), o jogo abre na aba atual.
    const tab = newTab ? window.open('', '_blank') : null;
    // Impede que a página do jogo acesse/redirecione a aba da plataforma.
    if (tab) tab.opener = null;

    try {
      const { redirect_url } = await createPartnerLink({ ssoToken, clientId: CLIENT_ID, state });

      if (tab) {
        tab.location.href = redirect_url;
        setLoading(false);
      } else {
        window.location.href = redirect_url;
      }
    } catch (error) {
      tab?.close();
      setLoading(false);

      // Token do SSO expirado ou ausente: só um novo login resolve.
      if (error?.response?.status === 401) {
        toast.error(t('heroisEnem.sessionExpired'));
        signOut();
        return;
      }

      const code = error?.response?.data?.error;
      toast.error(t(ERROR_MESSAGES[code] || 'heroisEnem.genericError'));
    }
  };

  return { canAccess, open, loading };
}
